import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Calendar,
  Loader2,
  FileText,
  Image,
  X,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);

  // Gallery state
  const [galleryImages, setGalleryImages] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);

  const emptyForm = {
    title: { fr: "", en: "" },
    excerpt: { fr: "", en: "" },
    content: { fr: "", en: "" },
    cover_image: "",
    image_url: "",
    category: "travel",
    tags: [],
    is_published: false,
  };

  const [form, setForm] = useState(emptyForm);
  const [tagsText, setTagsText] = useState("");

  useEffect(() => {
    fetchPosts();
  }, []);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
  });

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/blog`, getAuthHeader());
      setPosts(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchGallery = async () => {
    try {
      setLoadingGallery(true);
      const res = await axios.get(`${API}/admin/gallery`, getAuthHeader());
      // Filter only images (not videos)
      const images = res.data.filter(item => item.media_type !== 'video');
      setGalleryImages(images);
    } catch (err) {
      toast.error("Erreur lors du chargement de la galerie");
    } finally {
      setLoadingGallery(false);
    }
  };

  const openGalleryPicker = () => {
    fetchGallery();
    setShowGalleryPicker(true);
  };

  const selectGalleryImage = (imageUrl) => {
    setForm({ ...form, cover_image: imageUrl, image_url: imageUrl });
    setShowGalleryPicker(false);
    toast.success("Image sélectionnée");
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    // Use image_url from backend, fallback to cover_image for backward compatibility
    const imageUrl = post.image_url || post.cover_image || "";
    setForm({
      title: post.title || { fr: "", en: "" },
      excerpt: post.excerpt || { fr: "", en: "" },
      content: post.content || { fr: "", en: "" },
      cover_image: imageUrl,
      image_url: imageUrl,
      category: post.category || "travel",
      tags: post.tags || [],
      is_published: post.is_published || false,
    });
    setTagsText((post.tags || []).join(", "));
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingPost(null);
    setForm(emptyForm);
    setTagsText("");
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.fr) {
      toast.error("Le titre est requis");
      return;
    }

    const data = {
      ...form,
      tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
    };

    try {
      setSaving(true);
      if (editingPost) {
        await axios.put(
          `${API}/admin/blog/${editingPost.id}`,
          data,
          getAuthHeader()
        );
        toast.success("Article mis à jour");
      } else {
        await axios.post(`${API}/admin/blog`, data, getAuthHeader());
        toast.success("Article créé");
      }
      setShowForm(false);
      fetchPosts();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (postId, currentStatus) => {
    try {
      await axios.put(
        `${API}/admin/blog/${postId}`,
        { is_published: !currentStatus },
        getAuthHeader()
      );
      toast.success(currentStatus ? "Article dépublié" : "Article publié");
      fetchPosts();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await axios.delete(`${API}/admin/blog/${postId}`, getAuthHeader());
      toast.success("Article supprimé");
      fetchPosts();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR");
  };

  const categories = [
    { id: "travel", name: "Voyage" },
    { id: "culture", name: "Culture" },
    { id: "tips", name: "Conseils" },
    { id: "stories", name: "Récits" },
    { id: "news", name: "Actualités" },
  ];

  return (
    <div data-testid="admin-blog-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-serif font-bold">Blog</h1>
          <p className="text-muted-foreground">
            Gérez les articles du blog
          </p>
        </div>
        <Button onClick={handleCreate} data-testid="add-blog-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nouvel article
        </Button>
      </div>

      {/* Posts List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Aucun article</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {posts.map((post) => (
            <div
              key={post.id}
              className="bg-card border rounded-xl p-6"
              data-testid={`blog-item-${post.id}`}
            >
              <div className="flex flex-col md:flex-row gap-4">
                {/* Cover Image */}
                <div className="w-full md:w-40 h-28 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  <img
                    src={post.image_url || post.cover_image || "https://via.placeholder.com/300x200"}
                    alt={post.title?.fr}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <h3 className="font-semibold text-lg">
                        {post.title?.fr}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {post.excerpt?.fr}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {post.is_published ? (
                        <Badge variant="default" className="bg-green-500">
                          <Eye className="h-3 w-3 mr-1" />
                          Publié
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <EyeOff className="h-3 w-3 mr-1" />
                          Brouillon
                        </Badge>
                      )}
                      <Badge variant="outline">{post.category}</Badge>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDate(post.created_at)}
                    </span>
                    <span>Par: {post.author_name}</span>
                    <span>{post.views || 0} vues</span>
                  </div>

                  <div className="flex items-center justify-between">
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex gap-1">
                        {post.tags.slice(0, 3).map((tag, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            #{tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 ml-auto">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => togglePublish(post.id, post.is_published)}
                      >
                        {post.is_published ? (
                          <>
                            <EyeOff className="h-4 w-4 mr-1" />
                            Dépublier
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-1" />
                            Publier
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(post)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive"
                        onClick={() => handleDelete(post.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPost ? "Modifier l'article" : "Nouvel article"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            {/* Title */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Titre (FR) *</Label>
                <Input
                  value={form.title.fr}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      title: { ...form.title, fr: e.target.value },
                    })
                  }
                />
              </div>
              <div>
                <Label>Titre (EN)</Label>
                <Input
                  value={form.title.en}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      title: { ...form.title, en: e.target.value },
                    })
                  }
                />
              </div>
            </div>

            {/* Excerpt */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Extrait (FR)</Label>
                <Textarea
                  value={form.excerpt.fr}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      excerpt: { ...form.excerpt, fr: e.target.value },
                    })
                  }
                  rows={2}
                />
              </div>
              <div>
                <Label>Extrait (EN)</Label>
                <Textarea
                  value={form.excerpt.en}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      excerpt: { ...form.excerpt, en: e.target.value },
                    })
                  }
                  rows={2}
                />
              </div>
            </div>

            {/* Content FR */}
            <div>
              <Label>Contenu (FR)</Label>
              <Textarea
                value={form.content.fr}
                onChange={(e) =>
                  setForm({
                    ...form,
                    content: { ...form.content, fr: e.target.value },
                  })
                }
                rows={8}
                placeholder="Rédigez votre article ici..."
              />
            </div>

            {/* Content EN */}
            <div>
              <Label>Contenu (EN)</Label>
              <Textarea
                value={form.content.en}
                onChange={(e) =>
                  setForm({
                    ...form,
                    content: { ...form.content, en: e.target.value },
                  })
                }
                rows={6}
              />
            </div>

            {/* Category & Image */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Catégorie</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Image de couverture</Label>
                <div className="flex gap-2">
                  <Input
                    value={form.cover_image}
                    onChange={(e) =>
                      setForm({ ...form, cover_image: e.target.value, image_url: e.target.value })
                    }
                    placeholder="https://... ou choisir de la galerie"
                    className="flex-1"
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={openGalleryPicker}
                  >
                    <Image className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Image Preview */}
            {form.cover_image && (
              <div className="relative">
                <Label className="mb-2 block">Aperçu de l'image</Label>
                <div className="relative w-full h-48 rounded-lg overflow-hidden bg-muted">
                  <img
                    src={form.cover_image}
                    alt="Aperçu"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/800x400?text=Image+non+trouvée";
                    }}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2"
                    onClick={() => setForm({ ...form, cover_image: "", image_url: "" })}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Tags */}
            <div>
              <Label>Tags (séparés par des virgules)</Label>
              <Input
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                placeholder="voyage, sahara, aventure"
              />
            </div>

            {/* Publish toggle */}
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_published}
                onCheckedChange={(v) => setForm({ ...form, is_published: v })}
              />
              <Label>Publier immédiatement</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingPost ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gallery Picker Dialog */}
      <Dialog open={showGalleryPicker} onOpenChange={setShowGalleryPicker}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              Sélectionner une image de la galerie
            </DialogTitle>
          </DialogHeader>

          {loadingGallery ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : galleryImages.length === 0 ? (
            <div className="text-center py-12 bg-muted/30 rounded-xl">
              <Image className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Aucune image dans la galerie</p>
              <p className="text-sm text-muted-foreground mt-1">
                Ajoutez des images dans la section Galerie
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-[50vh] overflow-y-auto p-1">
              {galleryImages.map((image) => (
                <div
                  key={image.id}
                  className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all hover:border-primary ${
                    form.cover_image === image.file_url 
                      ? "border-primary ring-2 ring-primary/30" 
                      : "border-transparent"
                  }`}
                  onClick={() => selectGalleryImage(image.file_url)}
                >
                  <img
                    src={image.file_url}
                    alt={image.title?.fr || "Image"}
                    className="w-full h-full object-cover"
                  />
                  {form.cover_image === image.file_url && (
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                      <div className="bg-primary rounded-full p-2">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                    <p className="text-white text-xs truncate">
                      {image.title?.fr || image.category || "Image"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGalleryPicker(false)}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlog;
